import {createContext, useContext, useState, useEffect, useMemo} from 'react';
import {AnchorProvider, Program} from "@coral-xyz/anchor";
import type {ZenexPools} from "../components/SolanaPools/anchor-idl/idl.ts";

import {useAppKitProvider} from "@reown/appkit/react";
import type {Provider as AppKitProvider} from "@reown/appkit-adapter-solana/react";
import {AnchorWallet} from "@solana/wallet-adapter-react";
import {useAppKitConnection} from '@reown/appkit-adapter-solana/react'
import Idl from "../components/SolanaPools/anchor-idl/idl.json";
import {AnyTransaction} from "@reown/appkit-adapter-solana";


type ProgramContextType = {
  program?: Program<ZenexPools>;
};

const ProgramContext = createContext<ProgramContextType | undefined>(undefined);

type ProgramProviderProps = {
  children: React.ReactNode;
};

export const ProgramProvider: React.FC<ProgramProviderProps> = ({children}:ProgramProviderProps) => {
  const {connection} = useAppKitConnection();
  const {walletProvider} = useAppKitProvider<AppKitProvider>("solana");

  const [program, setProgram] = useState<Program<ZenexPools> | undefined>(undefined);

  const anchorWallet: AnchorWallet | undefined = useMemo(() => {
    if (
      walletProvider &&
      walletProvider.publicKey &&
      walletProvider.signTransaction &&
      walletProvider.signAllTransactions
    ) {
      return {
        publicKey: walletProvider.publicKey,
        signTransaction: walletProvider.signTransaction<AnyTransaction>,
        signAllTransactions: walletProvider.signAllTransactions<AnyTransaction[]>
      } as AnchorWallet;
    }
    return undefined;
  }, [walletProvider?.publicKey?.toBase58()]);

  useEffect(() => {
    const initProgram = async () => {
      if (connection && anchorWallet) {
        const provider = new AnchorProvider(connection, anchorWallet, {
          preflightCommitment: "confirmed"
        });

        const _program = new Program<ZenexPools>(Idl, provider);
        setProgram(_program);
      }
    };

    initProgram().catch(console.error);
  }, [anchorWallet?.publicKey?.toBase58(), connection]);

  return (
    <ProgramContext.Provider value={{program}}>
      {children}
    </ProgramContext.Provider>
  );
};

export const useProgram = () => {
  const ctx = useContext(ProgramContext);
  if (!ctx) {
    throw new Error("useProgram must be used within a ProgramProvider");
  }
  return ctx;
};